import { Canvas } from '@react-three/fiber'
import { Suspense } from 'react';
import { BaseFigure as Model, FigureOverlay, ModelLoading } from '../../components/';
import { Center, CameraControls } from '@react-three/drei';
import { theme } from '../../theme';
import { useState, useRef } from 'react';
import * as THREE from 'three'

const CURVE_POINTS = 200;
const Sphere = ({ radius = 1, segments = 20, cylinderRadius = 0.75, cylinderHeight = 1.25, highlightFigure = false, highlightFigure2 = false, highlightFigure3 = false }) => {
  const cylinderRef = useRef(null);
  const bottomRef = useRef(null);
  const meridians = [];
  // Meridians
  for (let i = 0; i < segments; i++) {
    const rotation = Math.PI * (i / segments);
    const curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      radius, radius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    meridians.push({ g: new THREE.BufferGeometry().setFromPoints(points), r: rotation });
  }
  // Parallels
  let parallels = [];
  for (let i = 0; i < segments; i++) {
    let pOffset = radius - radius / Math.pow(2, i);
    let pRadius = Math.sqrt(radius * radius - pOffset * pOffset);
    let curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      pRadius, pRadius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    parallels.push({ g: new THREE.BufferGeometry().setFromPoints(points), p: -pOffset });
  }
  const material = new THREE.LineBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.4});

  // Cylinder
  const cylinderMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(-0.5, 1.0, -vUv.y);

        gl_FragColor = vec4(vlak3color1, alpha);
      }
    `, 
  });
  // Cylinder Bottom
  const cylinderBottomMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(1.0, -1.0, -vUv.y);

        //gl_FragColor = vec4(vlak3color1, alpha);
        gl_FragColor = vec4(vlak3color1, 0.74);
      }
    `, 
  });

  const cylinderGeometry = new THREE.CylinderGeometry(cylinderRadius, cylinderRadius, cylinderHeight, 64, 1, true);
  const bottomGeometry = new THREE.CircleGeometry(cylinderRadius, 64);
  return (
    <mesh position={[0, radius - 0.01, 0]} castShadow>
      {meridians.map(({ g, r }, i) => (<line key={`mer-${i}`} rotation={[0, r, 0]} args={[g, material]} />))}
      {parallels.map(({ g, p }, i) => (<line key={`par-${i}`} position={[0, p, 0]} rotation={[Math.PI / 2, 0, 0]} args={[g, material]} />))}
      {highlightFigure && <mesh ref={cylinderRef} position={[0, -radius + cylinderHeight/2 + 0.02, -1]} args={[cylinderGeometry, cylinderMaterial]} />}
      {highlightFigure && <mesh ref={bottomRef} position={[0, -radius + 0.02, -1]} rotation={[Math.PI / 2, 0, 0]} args={[bottomGeometry, cylinderBottomMaterial]} />}

      {highlightFigure2 && <mesh ref={cylinderRef} position={[-1, -radius + cylinderHeight/2 + 0.02, 1]} args={[cylinderGeometry, cylinderMaterial]} />}
      {highlightFigure2 && <mesh ref={bottomRef} position={[-1, -radius + 0.02, 1]} rotation={[Math.PI / 2, 0, 0]} args={[bottomGeometry, cylinderBottomMaterial]} />}

      {highlightFigure3 && <mesh ref={cylinderRef} position={[1, -radius + cylinderHeight/2 + 0.02, 1]} args={[cylinderGeometry, cylinderMaterial]} />}
      {highlightFigure3 && <mesh ref={bottomRef} position={[1, -radius + 0.02, 1]} rotation={[Math.PI / 2, 0, 0]} args={[bottomGeometry, cylinderBottomMaterial]} />}
    </mesh>
  );
};

const Scene = ({ controls = null, animationType, figure, figure2, figure3, highlightFigure = false, highlightFigure2 = false, highlightFigure3 = false, cylinderRadius = 0.75, cylinderHeight = 1.25 }) => {
  const sphereRadius = 24;
  return (
    <>
      <ambientLight intensity={1} />
      <Suspense fallback={<FigureOverlay customStyle={{top: '-100px', left: '-120px'}}><ModelLoading /></FigureOverlay>}>
        <Center bottom position={[0, sphereRadius * 2 - 1, 0]}>
          <Sphere radius={sphereRadius} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight} highlightFigure={highlightFigure} highlightFigure2={highlightFigure2} highlightFigure3={highlightFigure3}/>
        </Center>
        <Model controls={controls} animationType={animationType} key={`figure-${figure.getId()}`} scale={figure.getScaleFactor()} glbPath={figure.getVrmSource()} rotation={[0, 0, 0]} position={[0, -1, -1]} objectGlbPath={null} weaponGlbPath={null} droneGlbPath={'/assets/boombox.glb'} droneType='dance'/>
        <Model controls={controls} animationType={animationType} key={`figure-${figure2.getId()}`} scale={figure2.getScaleFactor()} glbPath={figure2.getVrmSource()} rotation={[0, Math.PI / 1.5, 0]} position={[-1, -1, 1]} objectGlbPath={null} weaponGlbPath={'/assets/axe.glb'} weaponType='melee'/>
        <Model controls={controls} animationType={animationType} key={`figure-${figure3.getId()}`} scale={figure3.getScaleFactor()} glbPath={figure3.getVrmSource()} rotation={[0, -Math.PI / 1.5, 0]} position={[1, -1, 1]} weaponGlbPath={'/assets/shotty.glb'} objectGlbPath={'/assets/ramen.glb'} armorGlbPath={'/assets/legs.vrm'}/>
      </Suspense>
    </>
  );
}

const DECORATORS_THRESHOLD_MIN = 8;
const DECORATORS_THRESHOLD_MAX = 11;
const MOVEMENT_POSITIONS = {
  'figure1': {
    target: { x: 1.17, y: -0.13, z: -0.39 },
    position: { x: -0.56, y: 1.5, z: -10.46 }
  },
  'figure2': {
    target: { x: 0.19, y: -0.53, z: -1.5 },
    position: { x: -8.06, y: 1.9, z: 6.87 }
  },
  'figure3': {
    target: { x: -1.95, y: -0.51, z: 0.74 },
    position: { x: 9.17, y: 1.58, z: 4.75 }
  },
  'center': {
    target: { x: 0, y: -0.15, z: 0 },
    position: { x: -2.75, y: 1.25, z: -7.25 }
  },
  'home': {
    target: { x: -0.34, y: -0.10, z: 0.14 },
    position: { x: -3.37, y: 0.83, z: -7.08 }
  },
  'right': {
    target: { x: 1.92, y: -0.19, z: -0.16 },
    position: { x: -4.53, y: 0.84, z: -7.16 }
  },
  'left': {
    target: { x: -0.47, y: -0.08, z: -0.27 },
    position: { x: 3.92, y: 1.91, z: -7.89 }
  },
  'zoom': {
    target: { x: 0, y: 0.50, z: 0 },
    position: { x: -0.75, y: 0.60, z: -2.25 }
  }
};

export default function FigureView({ figure, figure2, figure3, animationType, objectPath, weaponPath, dronePath, width, height, noDecorators = false, controls = null, highlightedIndex = 0}) {
  const [dragging, setDragging] = useState(false);

  const [camera, setCamera] = useState(null);
  const sceneControlsRef = useRef(null);
  const figureControlsRef = useRef(null);
  const lightHolderRef = useRef(null);
  const cylinderRadius = 0.65;
  const cylinderHeight = 1;

  const [enabledControls, setEnableControls] = useState(true);

  const moveSmoothly = (newPosition, notSmooth = false) => {
    if (!figureControlsRef.current) return;
    const {target, position} = newPosition;
    figureControlsRef.current.setTarget(target.x, target.y, target.z, !notSmooth);
    figureControlsRef.current.setPosition(position.x, position.y, position.z, !notSmooth);
  }

  const moveAvatarLeft = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['left']);
    }
  };

  const moveAvatarRight = () => {
    if (figureControlsRef.current) {
      setEnableControls(false);
      moveSmoothly(MOVEMENT_POSITIONS['right']);
    }
  };

  const centerAvatar = (forHome = false) => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS[forHome ? 'home' : 'center'], forHome);
    }
  };

  const zoomToFace = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['zoom']);
    }
  }

  const selectFirstAvatar = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure1']);
    }
  };

  const selectSecondAvatar = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure2']);
    }
  };

  const selectThirdAvatar = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure3']);
    }
  };

  if (controls) {
    controls.moveAvatarLeft = moveAvatarLeft;
    controls.moveAvatarRight = moveAvatarRight;
    controls.centerAvatar = centerAvatar;
    controls.zoomToFace = zoomToFace;
    controls.selectFirstAvatar = selectFirstAvatar;
    controls.selectSecondAvatar = selectSecondAvatar;
    controls.selectThirdAvatar = selectThirdAvatar;
  }

  return (
    <div onMouseUp={() => setDragging(false)} onMouseDown={() => setDragging(true)} style={{cursor: dragging ? 'grabbing' : 'grab'}}>
      <Canvas gl={{antialias: true, logarithmicDepthBuffer: true}} shadows={true} onCreated={() => setTimeout(() => selectFirstAvatar(), 1000)} style={{ zIndex: 0, position: 'absolute', top: 0, left: 0, backgroundColor: theme.lightBlue, width: width, height }} shadows camera={{position:[-0.56, 1.5, -10.46], fov: 15 }}>
        <Scene animationType={animationType} controls={controls} figure={figure} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight} figure={figure} figure2={figure2} figure3={figure3} highlightFigure={highlightedIndex === 0} highlightFigure2={highlightedIndex === 1} highlightFigure3={highlightedIndex === 2} />
	<CameraControls ref={figureControlsRef} mouseButtons={{ left: 1, wheel: 0, right: 2 }} dollyToCursor={true} minDistance={1} maxDistance={100}/>
      </Canvas>
    </div>
  );
}
